import {useState} from 'react'
import {Pressable, View} from 'react-native'
import {type MessageDescriptor} from '@lingui/core'
import {msg} from '@lingui/core/macro'
import {Trans, useLingui} from '@lingui/react/macro'

import {
  OnboardingControls,
  OnboardingDescriptionText,
  OnboardingPosition,
  OnboardingTitleText,
} from '#/screens/Onboarding/Layout'
import {useOnboardingInternalState} from '#/screens/Onboarding/state'
import {
  COUNTRY_CURRICULA,
  LEARNING_STAGE_ICONS,
  LEARNING_STAGES,
  STAGES_NEEDING_GRADE_LEVEL,
} from '#/screens/Onboarding/StepGoal/data'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import * as Toggle from '#/components/forms/Toggle'
import {Text} from '#/components/Typography'

const STAGE_PHASE2_TITLE: Record<string, MessageDescriptor> = {
  'Preschool / Kindergarten': msg`You're All Set! 🌱`,
  'Primary school': msg`Pick Your Grade Level 📚`,
  'Middle School': msg`Pick Your Grade Level 📚`,
  'High School': msg`Pick Your Grade Level 📚`,
  University: msg`Higher Education Confirmed 🎓`,
  'Self-learner (autodidact)': msg`No Rules, Just Curiosity 🔥`,
  'Lifelong Learner': msg`Always Learning 🔄`,
}

const STAGE_PHASE2_SUBTITLE: Record<string, MessageDescriptor> = {
  'Preschool / Kindergarten': msg`No classroom structure here — we'll tailor everything to early discovery and wonder.`,
  University: msg`Your degree path is set — next, we'll go beyond the curriculum and into what drives you.`,
  'Self-learner (autodidact)': msg`You set your own pace — no grades, no classroom, no limits.`,
  'Lifelong Learner': msg`Learning is a lifestyle for you — let's shape it around your passions.`,
}

export function StepLearnerStage() {
  const {i18n, t: l} = useLingui()
  const t = useTheme()
  const {
    state: {goalStepResults: g},
    dispatch,
  } = useOnboardingInternalState()
  const country = g.country ?? 'OTHER'
  const curriculum = COUNTRY_CURRICULA[country] ?? COUNTRY_CURRICULA.OTHER

  const [stage, setStage] = useState<string | null>(g.learningStage)
  const [gradeLevel, setGradeLevel] = useState<string | null>(g.gradeLevel)

  const needsGrade = !!stage && STAGES_NEEDING_GRADE_LEVEL.includes(stage)
  const gradeOptions: string[] = needsGrade
    ? (curriculum.stages[stage]?.levels ?? [])
    : []

  const canContinue = !!stage && (!needsGrade || !!gradeLevel)

  function onStageChange(vals: string[]) {
    const selected = vals[vals.length - 1] ?? null
    setStage(selected)
    setGradeLevel(null)
  }

  function clearStage() {
    setStage(null)
    setGradeLevel(null)
  }

  function onContinue() {
    if (!stage) return
    dispatch({
      type: 'setGoalResults',
      results: {
        learningStage: stage,
        gradeLevel: needsGrade ? gradeLevel : null,
      },
    })
    dispatch({type: 'next'})
  }

  if (!stage) {
    return (
      <View style={[a.align_start, a.gap_sm]} testID="onboardingLearnerStage">
        <OnboardingPosition />
        <OnboardingTitleText>
          <Trans>Find Your Stage 📍</Trans>
        </OnboardingTitleText>
        <OnboardingDescriptionText>
          <Trans>
            Where are you in your learning journey? Pick your stage — we'll
            shape everything around it.
          </Trans>
        </OnboardingDescriptionText>

        <View style={[a.w_full, a.pt_lg, a.gap_sm]}>
          <Toggle.Group
            values={[]}
            onChange={onStageChange}
            label={l`Select your learning stage`}>
            <View style={[a.flex_row, a.flex_wrap, a.gap_sm]}>
              {LEARNING_STAGES.map(s => (
                <Toggle.Item key={s} name={s} label={s}>
                  {({selected}) => (
                    <View
                      style={[
                        a.px_md,
                        a.py_sm,
                        a.rounded_full,
                        a.border,
                        selected
                          ? {
                              borderColor: 'transparent',
                              ...t.atoms.bg_contrast_975,
                            }
                          : t.atoms.border_contrast_low,
                      ]}>
                      <Text
                        style={[
                          a.text_sm,
                          a.font_bold,
                          selected && t.atoms.text_inverted,
                        ]}
                        emoji>
                        {LEARNING_STAGE_ICONS[s]} {s}
                      </Text>
                    </View>
                  )}
                </Toggle.Item>
              ))}
            </View>
          </Toggle.Group>
        </View>

        <OnboardingControls.Portal>
          <Button
            disabled
            testID="onboardingContinue"
            color="primary"
            size="large"
            label={l`Continue to next step`}
            onPress={onContinue}>
            <ButtonText>
              <Trans>Continue</Trans>
            </ButtonText>
          </Button>
        </OnboardingControls.Portal>
      </View>
    )
  }

  // Phase 2 — stage is selected
  const phase2Title = STAGE_PHASE2_TITLE[stage] ?? msg`You're All Set! 🌱`

  const phase2Subtitle = needsGrade
    ? i18n._(
        msg`Aligning to ${curriculum.flag} ${curriculum.name}'s curriculum — select your exact year.`,
      )
    : STAGE_PHASE2_SUBTITLE[stage]
      ? i18n._(STAGE_PHASE2_SUBTITLE[stage])
      : ''

  return (
    <View style={[a.align_start, a.gap_sm]} testID="onboardingLearnerStage">
      <OnboardingPosition />
      <OnboardingTitleText>{i18n._(phase2Title)}</OnboardingTitleText>
      <OnboardingDescriptionText>{phase2Subtitle}</OnboardingDescriptionText>

      {/* Filled chip — tapping clears stage and returns to Phase 1 */}
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={l`Clear stage selection`}
        accessibilityHint={l`Tap to deselect and choose a different stage`}
        onPress={clearStage}
        style={[a.w_full]}>
        <View
          style={[
            a.flex_row,
            a.align_center,
            a.justify_between,
            {
              paddingVertical: 12,
              paddingHorizontal: 14,
              borderRadius: 10,
              borderWidth: 1,
              borderColor: t.atoms.border_contrast_low.borderColor,
              backgroundColor: t.atoms.bg_contrast_25.backgroundColor,
            },
          ]}>
          <Text style={[a.text_sm, a.font_bold]} emoji>
            {LEARNING_STAGE_ICONS[stage]} {stage}
          </Text>
          <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>×</Text>
        </View>
      </Pressable>

      {/* Grade accordion — only for school stages */}
      {needsGrade && gradeOptions.length > 0 && (
        <View
          style={[
            a.w_full,
            a.mt_xs,
            {
              borderWidth: 1,
              borderColor: t.atoms.border_contrast_low.borderColor,
              borderRadius: 12,
              overflow: 'hidden',
            },
          ]}>
          {/* Accordion header */}
          <View
            style={[
              a.flex_row,
              a.align_center,
              a.gap_md,
              {
                padding: 14,
                backgroundColor: t.atoms.bg_contrast_25.backgroundColor,
                borderBottomWidth: 1,
                borderBottomColor: t.atoms.border_contrast_low.borderColor,
              },
            ]}>
            {/* Circle indicator */}
            <View
              style={[
                {
                  width: 22,
                  height: 22,
                  borderRadius: 11,
                  alignItems: 'center' as const,
                  justifyContent: 'center' as const,
                  flexShrink: 0,
                  backgroundColor: gradeLevel
                    ? t.palette.primary_100
                    : t.palette.primary_500,
                },
              ]}>
              <Text
                style={[
                  {fontSize: 11},
                  gradeLevel ? {color: t.palette.primary_700} : {color: '#fff'},
                ]}>
                {gradeLevel ? '✓' : '2'}
              </Text>
            </View>
            <Text style={[a.text_sm, a.font_bold]}>
              <Trans>Select your grade level</Trans>
            </Text>
          </View>

          {/* Accordion body */}
          <View
            style={[
              {
                padding: 16,
                backgroundColor: t.atoms.bg.backgroundColor,
              },
            ]}>
            <Toggle.Group
              values={gradeLevel ? [gradeLevel] : []}
              onChange={vals => setGradeLevel(vals[vals.length - 1] ?? null)}
              label={l`Select your grade level`}>
              <View style={[a.flex_row, a.flex_wrap, a.gap_sm]}>
                {gradeOptions.map(grade => (
                  <Toggle.Item key={grade} name={grade} label={grade}>
                    {({selected}) => (
                      <View
                        style={[
                          a.px_md,
                          a.py_sm,
                          a.rounded_full,
                          a.border,
                          selected
                            ? {
                                borderColor: 'transparent',
                                ...t.atoms.bg_contrast_975,
                              }
                            : t.atoms.border_contrast_low,
                        ]}>
                        <Text
                          style={[
                            a.text_sm,
                            a.font_bold,
                            selected && t.atoms.text_inverted,
                          ]}>
                          {grade}
                        </Text>
                      </View>
                    )}
                  </Toggle.Item>
                ))}
              </View>
            </Toggle.Group>
          </View>
        </View>
      )}

      <OnboardingControls.Portal>
        <Button
          disabled={!canContinue}
          testID="onboardingContinue"
          color="primary"
          size="large"
          label={l`Continue to next step`}
          onPress={onContinue}>
          <ButtonText>
            <Trans>Continue</Trans>
          </ButtonText>
        </Button>
      </OnboardingControls.Portal>
    </View>
  )
}
